import { useCallback, useEffect } from "react"
import { postSubmit } from "@ui-seeker/utils"
import { BACKEND_APIS, TIME_OUT_STATUS, STATUS_START_DELAY } from "@constants"
import { homeRunningExecutions, homeRunningStatus } from "Pages/Main/Home/@signals"

// One status-multiple for every running row of the home table. Each row used to poll
// execution_info/status on its own every five seconds: with forty reports running that was
// forty requests per tick, enough to swamp a Cloud Run instance while it starts.
export default function useManageHomeStatus() {
	const getStatus = useCallback(() => {
		let isMounted = true

		const fetchStatus = () => {
			if (!isMounted) return
			if (document.hidden) {
				// a background tab keeps its timer but sends nothing; it catches up when shown again
				setTimeout(fetchStatus, TIME_OUT_STATUS)
				return
			}

			const executions = homeRunningExecutions.value
			if (!executions.length) return

			postSubmit(`${BACKEND_APIS.REPORTS}/status-multiple`, { executions })
				.then(res => {
					if (!isMounted) return
					homeRunningStatus.value = { ...homeRunningStatus.value, ...res }

					// rows the backend reported as completed (or did not know) leave the poll; rows that
					// registered while the request was in flight stay
					const pending = executions.filter(id => res[id] && !res[id].ExecutionGlobalStatusCompleted)
					const stillRunning = homeRunningExecutions.value.filter(id => !executions.includes(id) || pending.includes(id))
					if (stillRunning.length !== homeRunningExecutions.value.length) homeRunningExecutions.value = stillRunning

					if (homeRunningExecutions.value.length) setTimeout(fetchStatus, TIME_OUT_STATUS)
				})
				.catch(() => {
					if (isMounted) setTimeout(fetchStatus, TIME_OUT_STATUS)
				})
		}

		fetchStatus()

		return () => {
			isMounted = false
		}
	}, [homeRunningExecutions.value.length])

	useEffect(() => {
		// the rows register one by one on mount: the delay collapses them into one first request
		let stop
		const timer = setTimeout(() => { stop = getStatus() }, STATUS_START_DELAY)
		return () => {
			clearTimeout(timer)
			if (stop) stop()
		}
	}, [getStatus])
}
